import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useSelector } from 'react-redux'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  PlayIcon,
} from '@heroicons/react/24/outline'
import {
  FullDebugFile,
  VoicePack,
  useGetMoEvalsConfigQuery,
  useGetVoiceStatusQuery,
  useListVoicePacksQuery,
  useStartVoicePackMutation,
  useGetOrchStatusQuery,
  useListOrchPacksQuery,
  useStartOrchPackMutation,
  useGetClientLiveStatusQuery,
  useListClientLivePacksQuery,
  useStartClientLivePackMutation,
  useGetDetOrchStatusQuery,
  useListDetOrchPacksQuery,
  useStartDetOrchPackMutation,
  voiceFileDownloadUrl,
  orchFileDownloadUrl,
  clientLiveFileDownloadUrl,
  detOrchFileDownloadUrl,
} from '../store/api/moEvalsApi'
import type { RootState } from '../store'

type TabId = 'voice' | 'orchestration' | 'client_live' | 'det_orch'

const VOICE_LABELS: Record<string, string> = {
  '01_natural_user': 'Natural user',
  '02_challenge_adversarial': 'Adversarial',
  '03_challenge_wave2': 'Wave 2',
}

const ORCH_LABELS: Record<string, string> = {
  client_short: 'Client short',
  client_gibberish: 'Client gibberish',
  org_owner_detailed: 'Org owner',
  orch_runner: 'Runner log',
}

const CLIENT_LIVE_LABELS: Record<string, string> = {
  client_live: 'Client live',
  client_live_runner: 'Runner log',
  client_live_free: 'Free Mo',
  client_live_ops_coverage: 'Ops coverage',
  ops_ledger: 'Ops ledger',
  findings: 'Findings',
  insight: 'Insight',
  pack_insight: 'Pack insight',
  chatgpt_analysis_client_live: 'Analysis',
  chatgpt_analysis_client_live_free: 'Free Mo analysis',
  chatgpt_analysis_client_live_ops_coverage: 'Ops coverage analysis',
  client_live_ops_ledger: 'Ops ledger',
  client_live_findings: 'Findings',
  client_live_insight: 'Insight',
  client_live_free_insight: 'Free Mo insight',
  client_live_ops_coverage_insight: 'Ops coverage insight',
  client_live_free_findings: 'Free Mo findings',
  client_live_ops_coverage_findings: 'Ops coverage findings',
  client_live_free_turn_insights: 'Free Mo turn insights',
  client_live_ops_coverage_turn_insights: 'Ops coverage turn insights',
  turn_insights: 'Turn insights',
}

const DET_ORCH_LABELS: Record<string, string> = {
  det_orch_runner: 'Runner log',
  det_orch: 'Det orch 3x',
  R1: 'R1 autonomous-preview',
  R2: 'R2 human-assistance',
  R3: 'R3 migration-preview-retry',
  summary: 'Summary',
  runs: 'Runs',
  failures: 'Failures',
  mo_orchestration_deterministic_3x_summary: 'Summary',
  mo_orchestration_deterministic_3x_runs: 'Runs',
  mo_orchestration_deterministic_3x_failures: 'Failures',
}

function formatBytes(n?: number) {
  if (n == null || !Number.isFinite(n)) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}

function copyText(text: string, label = 'Copied') {
  navigator.clipboard.writeText(text).then(
    () => toast.success(label),
    () => toast.error('Could not copy'),
  )
}

async function downloadWithAuth(url: string, fileName: string, token: string | null) {
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) {
    toast.error(`Download failed (${res.status})`)
    return
  }
  const blob = await res.blob()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(a.href)
  toast.success('Downloaded')
}

function StatePill({ state }: { state?: string }) {
  const s = state || '—'
  const cls =
    s === 'done'
      ? 'bg-green-100 text-green-800'
      : s === 'failed'
        ? 'bg-red-100 text-red-800'
        : s === 'running'
          ? 'bg-blue-100 text-blue-800'
          : 'bg-gray-100 text-gray-600'
  return <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{s}</span>
}

const MoEvals: React.FC = () => {
  const token = useSelector((s: RootState) => s.auth.token)
  const [tab, setTab] = useState<TabId>('voice')
  const [selectedVoicePack, setSelectedVoicePack] = useState<string | null>(null)
  const [selectedOrchPack, setSelectedOrchPack] = useState<string | null>(null)
  const [selectedClientLivePack, setSelectedClientLivePack] = useState<string | null>(null)
  const [selectedDetOrchPack, setSelectedDetOrchPack] = useState<string | null>(null)
  const [voiceLogKey, setVoiceLogKey] = useState('01_natural_user')
  const [orchLogKey, setOrchLogKey] = useState('orch_runner')
  const [clientLiveLogKey, setClientLiveLogKey] = useState('client_live_runner')
  // The deterministic orchestration pack writes one live log (no picker for it).
  const detOrchLogKey = 'det_orch_runner'

  const { data: configData } = useGetMoEvalsConfigQuery()

  const { data: voicePacksData, refetch: refetchVoicePacks } = useListVoicePacksQuery(undefined, {
    pollingInterval: tab === 'voice' ? 8000 : 0,
  })
  const { data: voiceStatusData, refetch: refetchVoiceStatus } = useGetVoiceStatusQuery(undefined, {
    pollingInterval: tab === 'voice' ? 2500 : 0,
  })
  const [startVoicePack, { isLoading: startingVoice }] = useStartVoicePackMutation()

  const { data: orchPacksData, refetch: refetchOrchPacks } = useListOrchPacksQuery(undefined, {
    pollingInterval: tab === 'orchestration' ? 8000 : 0,
  })
  const { data: orchStatusData, refetch: refetchOrchStatus } = useGetOrchStatusQuery(undefined, {
    pollingInterval: tab === 'orchestration' ? 2500 : 0,
  })
  const [startOrchPack, { isLoading: startingOrch }] = useStartOrchPackMutation()

  const { data: clientLivePacksData, refetch: refetchClientLivePacks } = useListClientLivePacksQuery(undefined, {
    pollingInterval: tab === 'client_live' ? 8000 : 0,
  })
  const { data: clientLiveStatusData, refetch: refetchClientLiveStatus } = useGetClientLiveStatusQuery(undefined, {
    pollingInterval: tab === 'client_live' ? 2500 : 0,
  })
  const [startClientLivePack, { isLoading: startingClientLive }] = useStartClientLivePackMutation()

  const { data: detOrchPacksData, refetch: refetchDetOrchPacks } = useListDetOrchPacksQuery(undefined, {
    pollingInterval: tab === 'det_orch' ? 5000 : 0,
  })
  const { data: detOrchStatusData, refetch: refetchDetOrchStatus } = useGetDetOrchStatusQuery(undefined, {
    pollingInterval: tab === 'det_orch' ? 2000 : 0,
  })
  const [startDetOrchPack, { isLoading: startingDetOrch }] = useStartDetOrchPackMutation()

  const voiceStatus = voiceStatusData?.data
  const voicePacks = voicePacksData?.data || []
  const voiceRunning = !!voiceStatus?.running

  const orchStatus = orchStatusData?.data
  const orchPacks = orchPacksData?.data || []
  const orchRunning = !!orchStatus?.running

  const clientLiveStatus = clientLiveStatusData?.data
  const clientLivePacks = clientLivePacksData?.data || []
  const clientLiveRunning = !!clientLiveStatus?.running

  const detOrchStatus = detOrchStatusData?.data
  const detOrchPacks = detOrchPacksData?.data || []
  const detOrchRunning = !!detOrchStatus?.running

  useEffect(() => {
    if (voiceStatus?.pack?.packStamp) {
      setSelectedVoicePack(voiceStatus.pack.packStamp)
    } else if (!selectedVoicePack && voicePacks[0]?.packStamp) {
      setSelectedVoicePack(voicePacks[0].packStamp)
    }
  }, [voiceStatus?.pack?.packStamp, voicePacks, selectedVoicePack])

  useEffect(() => {
    if (orchStatus?.pack?.packStamp) {
      setSelectedOrchPack(orchStatus.pack.packStamp)
    } else if (!selectedOrchPack && orchPacks[0]?.packStamp) {
      setSelectedOrchPack(orchPacks[0].packStamp)
    }
  }, [orchStatus?.pack?.packStamp, orchPacks, selectedOrchPack])

  useEffect(() => {
    if (clientLiveStatus?.pack?.packStamp) {
      setSelectedClientLivePack(clientLiveStatus.pack.packStamp)
    } else if (!selectedClientLivePack && clientLivePacks[0]?.packStamp) {
      setSelectedClientLivePack(clientLivePacks[0].packStamp)
    }
  }, [clientLiveStatus?.pack?.packStamp, clientLivePacks, selectedClientLivePack])

  useEffect(() => {
    if (detOrchStatus?.pack?.packStamp) {
      setSelectedDetOrchPack(detOrchStatus.pack.packStamp)
    } else if (!selectedDetOrchPack && detOrchPacks[0]?.packStamp) {
      setSelectedDetOrchPack(detOrchPacks[0].packStamp)
    }
  }, [detOrchStatus?.pack?.packStamp, detOrchPacks, selectedDetOrchPack])

  const activeVoicePack: VoicePack | null = useMemo(() => {
    if (!selectedVoicePack) return voicePacks[0] || null
    return (
      voicePacks.find(
        (p) =>
          p.packStamp === selectedVoicePack || p.folderName === `chatgpt_pack_${selectedVoicePack}`,
      ) || null
    )
  }, [voicePacks, selectedVoicePack])

  const activeOrchPack: VoicePack | null = useMemo(() => {
    if (!selectedOrchPack) return orchPacks[0] || null
    return (
      orchPacks.find(
        (p) =>
          p.packStamp === selectedOrchPack
          || p.folderName === `chatgpt_orch_pack_${selectedOrchPack}`
          || p.folderName === `orch_${selectedOrchPack}`,
      ) || null
    )
  }, [orchPacks, selectedOrchPack])

  const activeClientLivePack: VoicePack | null = useMemo(() => {
    if (!selectedClientLivePack) return clientLivePacks[0] || null
    return (
      clientLivePacks.find(
        (p) =>
          p.packStamp === selectedClientLivePack
          || p.folderName === `chatgpt_client_live_pack_${selectedClientLivePack}`,
      ) || null
    )
  }, [clientLivePacks, selectedClientLivePack])

  const activeDetOrchPack: VoicePack | null = useMemo(() => {
    if (!selectedDetOrchPack) return detOrchPacks[0] || null
    return (
      detOrchPacks.find(
        (p) =>
          p.packStamp === selectedDetOrchPack
          || p.folderName === `det_orch_3x_${selectedDetOrchPack}`,
      ) || null
    )
  }, [detOrchPacks, selectedDetOrchPack])

  const voiceLiveLog = voiceStatus?.evalLogs?.[voiceLogKey] || voiceStatus?.logTail || ''
  const orchLiveLog =
    orchStatus?.evalLogs?.[orchLogKey]
    || orchStatus?.evalLogs?.orch_runner
    || orchStatus?.logTail
    || ''
  const clientLiveLog =
    clientLiveStatus?.evalLogs?.[clientLiveLogKey]
    || clientLiveStatus?.evalLogs?.client_live_runner
    || clientLiveStatus?.logTail
    || ''
  const detOrchLog =
    detOrchStatus?.evalLogs?.[detOrchLogKey]
    || detOrchStatus?.evalLogs?.det_orch_runner
    || detOrchStatus?.logTail
    || ''

  const onRunVoice = async () => {
    try {
      const res = await startVoicePack().unwrap()
      toast.success(res?.data?.message || 'Started 3 Mo Voice evals in parallel')
      refetchVoiceStatus()
      refetchVoicePacks()
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || 'Failed to start')
    }
  }

  const onRunOrch = async () => {
    try {
      const res = await startOrchPack().unwrap()
      toast.success(res?.data?.message || 'Started Mo Orchestration pack')
      refetchOrchStatus()
      refetchOrchPacks()
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || 'Failed to start')
    }
  }

  const onRunClientLive = async () => {
    try {
      const res = await startClientLivePack().unwrap()
      toast.success(res?.data?.message || 'Started Client Live pack')
      refetchClientLiveStatus()
      refetchClientLivePacks()
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || 'Failed to start')
    }
  }

  const onRunDetOrch = async () => {
    try {
      const res = await startDetOrchPack().unwrap()
      toast.success(res?.data?.message || 'Started Deterministic Orch 3x')
      refetchDetOrchStatus()
      refetchDetOrchPacks()
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || 'Failed to start')
    }
  }

  const orchFiles = (pack: VoicePack | null): FullDebugFile[] =>
    pack?.chatgptAnalysisFiles?.length
      ? pack.chatgptAnalysisFiles
      : pack?.fullDebugFiles || []

  const clientLiveFiles = (pack: VoicePack | null): FullDebugFile[] =>
    pack?.chatgptAnalysisFiles?.length
      ? pack.chatgptAnalysisFiles
      : pack?.fullDebugFiles || []

  const detOrchFiles = (pack: VoicePack | null): FullDebugFile[] =>
    pack?.chatgptAnalysisFiles?.length
      ? pack.chatgptAnalysisFiles
      : pack?.fullDebugFiles || []

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Mo Evals</h1>
          <p className="text-sm text-gray-600 mt-1">
            Run shadow eval packs and grab ChatGPT debug JSONs without touching your live Mo rail.
          </p>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <button
            type="button"
            onClick={() => setTab('voice')}
            className={`pb-3 text-sm font-medium border-b-2 ${
              tab === 'voice'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            Mo voice
          </button>
          <button
            type="button"
            onClick={() => setTab('orchestration')}
            className={`pb-3 text-sm font-medium border-b-2 ${
              tab === 'orchestration'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            Mo orchestration
          </button>
          <button
            type="button"
            onClick={() => setTab('client_live')}
            className={`pb-3 text-sm font-medium border-b-2 ${
              tab === 'client_live'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            Client live
          </button>
          <button
            type="button"
            onClick={() => setTab('det_orch')}
            className={`pb-3 text-sm font-medium border-b-2 ${
              tab === 'det_orch'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            Det orch 3x
          </button>
        </nav>
      </div>

      {tab === 'det_orch' ? (
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Deterministic orchestration 3x</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Fast contract eval (R1/R2/R3). Forced SemanticDecision + real production plumbing.
                  Does not run Client Live.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <StatePill state={detOrchRunning ? 'running' : (activeDetOrchPack as any)?.pass === false ? 'failed' : activeDetOrchPack ? 'done' : undefined} />
                <button
                  type="button"
                  disabled={detOrchRunning || startingDetOrch}
                  onClick={onRunDetOrch}
                  className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  <PlayIcon className="h-4 w-4" />
                  {startingDetOrch || detOrchRunning ? 'Running…' : 'Run det orch 3x'}
                </button>
                <button
                  type="button"
                  onClick={() => { refetchDetOrchPacks(); refetchDetOrchStatus() }}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-2 text-sm text-gray-700"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {detOrchPacks.slice(0, 12).map((p) => (
                <button
                  key={p.folderName || p.packStamp}
                  type="button"
                  onClick={() => setSelectedDetOrchPack(p.packStamp)}
                  className={`rounded-md border px-2 py-1 text-xs ${
                    selectedDetOrchPack === p.packStamp
                      ? 'border-blue-500 bg-blue-50 text-blue-800'
                      : 'border-gray-200 text-gray-700'
                  }`}
                >
                  {p.packStamp}
                  {(p as any).pass === false ? ' FAIL' : (p as any).pass ? ' PASS' : ''}
                </button>
              ))}
            </div>
            <pre className="max-h-64 overflow-auto rounded bg-gray-900 p-3 text-xs text-green-100 whitespace-pre-wrap">
              {detOrchLog || 'No live log yet.'}
            </pre>
            <div className="space-y-2">
              {(detOrchFiles(activeDetOrchPack) || []).map((f) => (
                <div key={f.file} className="flex items-center justify-between gap-2 text-sm">
                  <span>{DET_ORCH_LABELS[f.key || ''] || f.label || f.file}</span>
                  <span className="text-gray-500">{formatBytes(f.bytes)}</span>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-blue-700"
                    onClick={() => downloadWithAuth(
                      detOrchFileDownloadUrl(selectedDetOrchPack || activeDetOrchPack?.packStamp || '', f.file),
                      f.file,
                      token,
                    )}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : tab === 'client_live' ? (
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Client live pack</h2>
                <p className="text-sm text-gray-600">
                  Standalone realistic CLIENT pack (two scenarios: free Mo + ops pressure). Mo is
                  unrestricted; scoring is post-hoc. Downloads include findings, ops ledger, and full
                  agent work.
                </p>
              </div>
              <button
                type="button"
                onClick={onRunClientLive}
                disabled={startingClientLive || clientLiveRunning || configData?.data?.apiRootExists === false}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {clientLiveRunning || startingClientLive ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <PlayIcon className="h-4 w-4" />
                )}
                {clientLiveRunning ? 'Running…' : startingClientLive ? 'Starting…' : 'Run client live'}
              </button>
            </div>

            {configData?.data?.apiRootExists === false && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                motionode_api root not found at <code>{configData?.data?.apiRoot}</code>. Set{' '}
                <code>MO_EVAL_API_ROOT</code> on the admin backend.
              </p>
            )}

            {clientLiveStatus?.pack && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(clientLiveStatus.pack.evals || []).map((ev) => (
                  <div key={ev.key} className="rounded border border-gray-200 p-3 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {CLIENT_LIVE_LABELS[ev.key] || ev.label || ev.key}
                      </span>
                      <StatePill state={ev.state} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {ev.file || `chatgpt_analysis_${ev.key}.json`}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm font-medium text-gray-800">Live logs</span>
                {(['client_live_runner', 'client_live_free', 'client_live_ops_coverage'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setClientLiveLogKey(k)}
                    className={`text-xs px-2 py-1 rounded ${
                      clientLiveLogKey === k ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {CLIENT_LIVE_LABELS[k]}
                  </button>
                ))}
              </div>
              <pre className="h-56 overflow-auto rounded bg-gray-900 text-green-100 text-xs p-3 whitespace-pre-wrap">
                {clientLiveLog
                  || (clientLiveRunning
                    ? 'Waiting for log output…'
                    : 'No active run. Click “Run client live”.')}
              </pre>
              {clientLiveStatus?.pack?.packDir && (
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                  <span className="truncate">Pack: {clientLiveStatus.pack.packDir}</span>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-blue-700"
                    onClick={() => copyText(clientLiveStatus.pack!.packDir, 'Pack path copied')}
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                    Copy path
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Past packs / ChatGPT + ops ledger</h2>
              <button
                type="button"
                onClick={() => refetchClientLivePacks()}
                className="text-sm text-blue-700 inline-flex items-center gap-1"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Refresh
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {clientLivePacks.map((p) => (
                <button
                  key={p.folderName}
                  type="button"
                  onClick={() => setSelectedClientLivePack(p.packStamp)}
                  className={`text-xs px-3 py-1.5 rounded border ${
                    activeClientLivePack?.packStamp === p.packStamp
                      ? 'border-blue-600 bg-blue-50 text-blue-800'
                      : 'border-gray-200 bg-white text-gray-700'
                  }`}
                >
                  {p.packStamp.slice(0, 19)} · {p.phase}
                </button>
              ))}
              {!clientLivePacks.length && (
                <p className="text-sm text-gray-500">
                  No chatgpt_client_live_pack_* folders yet.
                </p>
              )}
            </div>

            {activeClientLivePack && (
              <div className="space-y-3">
                <div className="text-sm text-gray-700">
                  <div className="font-medium">{activeClientLivePack.folderName}</div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    <span className="truncate">{activeClientLivePack.packDir}</span>
                    <button
                      type="button"
                      className="text-blue-700 inline-flex items-center gap-1"
                      onClick={() => copyText(activeClientLivePack.packDir, 'Pack path copied')}
                    >
                      <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      Copy
                    </button>
                  </div>
                  {Array.isArray(activeClientLivePack.results) && activeClientLivePack.results.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-gray-700 list-disc pl-4">
                      {activeClientLivePack.results.map((r: any) => (
                        <li key={r.scenarioId}>
                          <span className="font-medium">{r.scenarioId}</span>
                          {r.insightOneLiner ? `: ${r.insightOneLiner}` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="divide-y divide-gray-100 border border-gray-200 rounded">
                  {clientLiveFiles(activeClientLivePack).map((f: FullDebugFile) => (
                    <div key={f.file} className="flex flex-wrap items-center justify-between gap-2 p-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{f.file}</div>
                        <div className="text-xs text-gray-500">
                          {f.label || CLIENT_LIVE_LABELS[f.key || ''] || f.key} · {formatBytes(f.bytes)}
                          {f.turnCount != null ? ` · ${f.turnCount} turns` : ''}
                        </div>
                        {f.path && (
                          <div className="text-xs text-gray-400 truncate mt-0.5">{f.path}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {f.path && (
                          <button
                            type="button"
                            className="text-xs text-blue-700 inline-flex items-center gap-1"
                            onClick={() => copyText(f.path!, 'File path copied')}
                          >
                            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                            Path
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            downloadWithAuth(
                              clientLiveFileDownloadUrl(activeClientLivePack.packStamp, f.file),
                              f.file,
                              token,
                            )
                          }
                          className="inline-flex items-center gap-1 rounded bg-gray-900 text-white text-xs px-3 py-1.5 hover:bg-gray-800"
                        >
                          <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                  {!clientLiveFiles(activeClientLivePack).length && (
                    <p className="p-3 text-sm text-gray-500">
                      No analysis / ops ledger files in this pack yet.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : tab === 'orchestration' ? (
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Mo Orchestration pack</h2>
                <p className="text-sm text-gray-600">
                  Runs client-short + client-gibberish + org-owner scenarios (virtual board, simulated
                  agents), then packages three{' '}
                  <code className="text-xs bg-gray-100 px-1 rounded">chatgpt_analysis_*.json</code>{' '}
                  files for ChatGPT.
                </p>
              </div>
              <button
                type="button"
                onClick={onRunOrch}
                disabled={startingOrch || orchRunning || configData?.data?.apiRootExists === false}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {orchRunning || startingOrch ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <PlayIcon className="h-4 w-4" />
                )}
                {orchRunning ? 'Running…' : startingOrch ? 'Starting…' : 'Run all 3 personas'}
              </button>
            </div>

            {configData?.data?.apiRootExists === false && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                motionode_api root not found at <code>{configData?.data?.apiRoot}</code>. Set{' '}
                <code>MO_EVAL_API_ROOT</code> on the admin backend.
              </p>
            )}

            {orchStatus?.pack && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(orchStatus.pack.evals || []).map((ev) => (
                  <div key={ev.key} className="rounded border border-gray-200 p-3 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {ORCH_LABELS[ev.key] || ev.label || ev.key}
                      </span>
                      <StatePill state={ev.state} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {ev.file || `chatgpt_analysis_${ev.key}.json`}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm font-medium text-gray-800">Live logs</span>
                {(['orch_runner', 'client_short', 'client_gibberish', 'org_owner_detailed'] as const).map(
                  (k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setOrchLogKey(k)}
                      className={`text-xs px-2 py-1 rounded ${
                        orchLogKey === k ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {ORCH_LABELS[k]}
                    </button>
                  ),
                )}
              </div>
              <pre className="h-56 overflow-auto rounded bg-gray-900 text-green-100 text-xs p-3 whitespace-pre-wrap">
                {orchLiveLog
                  || (orchRunning
                    ? 'Waiting for log output…'
                    : 'No active run. Click “Run all 3 personas”.')}
              </pre>
              {orchStatus?.pack?.packDir && (
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                  <span className="truncate">Pack: {orchStatus.pack.packDir}</span>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-blue-700"
                    onClick={() => copyText(orchStatus.pack!.packDir, 'Pack path copied')}
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                    Copy path
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Past packs / ChatGPT files</h2>
              <button
                type="button"
                onClick={() => refetchOrchPacks()}
                className="text-sm text-blue-700 inline-flex items-center gap-1"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Refresh
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {orchPacks.map((p) => (
                <button
                  key={p.folderName}
                  type="button"
                  onClick={() => setSelectedOrchPack(p.packStamp)}
                  className={`text-xs px-3 py-1.5 rounded border ${
                    activeOrchPack?.packStamp === p.packStamp
                      ? 'border-blue-600 bg-blue-50 text-blue-800'
                      : 'border-gray-200 bg-white text-gray-700'
                  }`}
                >
                  {p.packStamp.slice(0, 19)} · {p.phase}
                  {p.kind === 'orch_run' ? ' · legacy' : ''}
                </button>
              ))}
              {!orchPacks.length && (
                <p className="text-sm text-gray-500">
                  No chatgpt_orch_pack_* / orch_* folders with analysis JSONs yet.
                </p>
              )}
            </div>

            {activeOrchPack && (
              <div className="space-y-3">
                <div className="text-sm text-gray-700">
                  <div className="font-medium">{activeOrchPack.folderName}</div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    <span className="truncate">{activeOrchPack.packDir}</span>
                    <button
                      type="button"
                      className="text-blue-700 inline-flex items-center gap-1"
                      onClick={() => copyText(activeOrchPack.packDir, 'Pack path copied')}
                    >
                      <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      Copy
                    </button>
                  </div>
                </div>

                <div className="divide-y divide-gray-100 border border-gray-200 rounded">
                  {orchFiles(activeOrchPack).map((f: FullDebugFile) => (
                    <div key={f.file} className="flex flex-wrap items-center justify-between gap-2 p-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{f.file}</div>
                        <div className="text-xs text-gray-500">
                          {f.label || ORCH_LABELS[f.key || ''] || f.key} · {formatBytes(f.bytes)}
                          {f.turnCount != null ? ` · ${f.turnCount} turns` : ''}
                          {f.semantics ? ` · ${f.semantics}` : ''}
                        </div>
                        {f.path && (
                          <div className="text-xs text-gray-400 truncate mt-0.5">{f.path}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {f.path && (
                          <button
                            type="button"
                            className="text-xs text-blue-700 inline-flex items-center gap-1"
                            onClick={() => copyText(f.path!, 'File path copied')}
                          >
                            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                            Path
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            downloadWithAuth(
                              orchFileDownloadUrl(activeOrchPack.packStamp, f.file),
                              f.file,
                              token,
                            )
                          }
                          className="inline-flex items-center gap-1 rounded bg-gray-900 text-white text-xs px-3 py-1.5 hover:bg-gray-800"
                        >
                          <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                  {!orchFiles(activeOrchPack).length && (
                    <p className="p-3 text-sm text-gray-500">
                      No chatgpt_analysis_*.json files in this pack yet.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Mo Voice pack</h2>
                <p className="text-sm text-gray-600">
                  Runs natural + adversarial + wave2 <strong>in parallel</strong>, then packages the three{' '}
                  <code className="text-xs bg-gray-100 px-1 rounded">*_FULL_DEBUG_for_chatgpt.json</code> files.
                </p>
              </div>
              <button
                type="button"
                onClick={onRunVoice}
                disabled={startingVoice || voiceRunning || configData?.data?.apiRootExists === false}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {voiceRunning || startingVoice ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <PlayIcon className="h-4 w-4" />
                )}
                {voiceRunning ? 'Running…' : startingVoice ? 'Starting…' : 'Run all 3'}
              </button>
            </div>

            {configData?.data?.apiRootExists === false && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                motionode_api root not found at <code>{configData?.data?.apiRoot}</code>. Set{' '}
                <code>MO_EVAL_API_ROOT</code> on the admin backend.
              </p>
            )}

            {voiceStatus?.pack && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(voiceStatus.pack.evals || []).map((ev) => (
                  <div key={ev.key} className="rounded border border-gray-200 p-3 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {VOICE_LABELS[ev.key] || ev.key}
                      </span>
                      <StatePill state={ev.state} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">challenge={ev.challenge}</p>
                  </div>
                ))}
              </div>
            )}

            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-gray-800">Live logs</span>
                {(['01_natural_user', '02_challenge_adversarial', '03_challenge_wave2'] as const).map(
                  (k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setVoiceLogKey(k)}
                      className={`text-xs px-2 py-1 rounded ${
                        voiceLogKey === k ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {VOICE_LABELS[k]}
                    </button>
                  ),
                )}
              </div>
              <pre className="h-56 overflow-auto rounded bg-gray-900 text-green-100 text-xs p-3 whitespace-pre-wrap">
                {voiceLiveLog || (voiceRunning ? 'Waiting for log output…' : 'No active run. Click “Run all 3”.')}
              </pre>
              {voiceStatus?.pack?.packDir && (
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                  <span className="truncate">Pack: {voiceStatus.pack.packDir}</span>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-blue-700"
                    onClick={() => copyText(voiceStatus.pack!.packDir, 'Pack path copied')}
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                    Copy path
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Past packs / ChatGPT files</h2>
              <button
                type="button"
                onClick={() => refetchVoicePacks()}
                className="text-sm text-blue-700 inline-flex items-center gap-1"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Refresh
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {voicePacks.map((p) => (
                <button
                  key={p.folderName}
                  type="button"
                  onClick={() => setSelectedVoicePack(p.packStamp)}
                  className={`text-xs px-3 py-1.5 rounded border ${
                    activeVoicePack?.packStamp === p.packStamp
                      ? 'border-blue-600 bg-blue-50 text-blue-800'
                      : 'border-gray-200 bg-white text-gray-700'
                  }`}
                >
                  {p.packStamp.slice(0, 19)} · {p.phase}
                </button>
              ))}
              {!voicePacks.length && (
                <p className="text-sm text-gray-500">No chatgpt_pack_* folders found yet.</p>
              )}
            </div>

            {activeVoicePack && (
              <div className="space-y-3">
                <div className="text-sm text-gray-700">
                  <div className="font-medium">{activeVoicePack.folderName}</div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    <span className="truncate">{activeVoicePack.packDir}</span>
                    <button
                      type="button"
                      className="text-blue-700 inline-flex items-center gap-1"
                      onClick={() => copyText(activeVoicePack.packDir, 'Pack path copied')}
                    >
                      <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      Copy
                    </button>
                  </div>
                </div>

                <div className="divide-y divide-gray-100 border border-gray-200 rounded">
                  {(activeVoicePack.fullDebugFiles || []).map((f: FullDebugFile) => (
                    <div key={f.file} className="flex flex-wrap items-center justify-between gap-2 p-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{f.file}</div>
                        <div className="text-xs text-gray-500">
                          {f.label || VOICE_LABELS[f.key || ''] || f.key} · {formatBytes(f.bytes)}
                          {f.semantics ? ` · ${f.semantics}` : ''}
                        </div>
                        {f.path && (
                          <div className="text-xs text-gray-400 truncate mt-0.5">{f.path}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {f.path && (
                          <button
                            type="button"
                            className="text-xs text-blue-700 inline-flex items-center gap-1"
                            onClick={() => copyText(f.path!, 'File path copied')}
                          >
                            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                            Path
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            downloadWithAuth(
                              voiceFileDownloadUrl(activeVoicePack.packStamp, f.file),
                              f.file,
                              token,
                            )
                          }
                          className="inline-flex items-center gap-1 rounded bg-gray-900 text-white text-xs px-3 py-1.5 hover:bg-gray-800"
                        >
                          <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                  {!(activeVoicePack.fullDebugFiles || []).length && (
                    <p className="p-3 text-sm text-gray-500">No FULL_DEBUG files in this pack yet.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MoEvals
